import type {
	BoundActions,
	HookActionsFunction,
	HookStateFunction,
	OverrideContainerComponent,
} from 'react-sweet-state';
import {
	createContainer,
	createStore,
	createActionsHook,
	createStateHook,
} from 'react-sweet-state';
import type { FC } from 'react';
import React from 'react';

export type InProductHelpContextType = {
	articleId?: string;
	routeName?: string;
	routeGroup?: string;
};

// testing purposes
export const getContextValue = (
	articleId: string,
	setArticleId: (articleId: string) => void,
	routeName: string,
	setRouteName: (routeName: string) => void,
	routeGroup: string,
	setRouteGroup: (routeGroup: string) => void,
): {
	getArticleId: () => string;
	setArticleId: (articleId: string) => void;
	getRouteName: () => string;
	setRouteName: (routeName: string) => void;
	getRouteGroup: () => string;
	setRouteGroup: (routeGroup: string) => void;
} => ({
	getArticleId: (): string => articleId,
	setArticleId,
	getRouteName: (): string => routeName,
	setRouteName,
	getRouteGroup: (): string => routeGroup,
	setRouteGroup,
});

type State = {
	articleId: string;
	routeName: string;
	routeGroup: string;
};

const initialInProductHelpState: State = {
	articleId: '',
	routeName: '',
	routeGroup: '',
};

const inProductHelpActions = {
	setArticleId:
		(articleId: string) =>
		({ setState }: { setState: any }): void => {
			setState({ articleId });
		},
	setRouteName:
		(routeName: string) =>
		({ setState }: { setState: any }): void => {
			setState({ routeName });
		},
	setRouteGroup:
		(routeGroup: string) =>
		({ setState }: { setState: any }): void => {
			setState({ routeGroup });
		},
};

const Store = createStore({
	initialState: {
		...initialInProductHelpState,
	},
	actions: inProductHelpActions,
	name: 'inProductHelpContext',
});

export const useInProductHelpActions: HookActionsFunction<
	BoundActions<
		{
			articleId: string;
			routeName: string;
			routeGroup: string;
		},
		{
			setArticleId: (articleId: string) => ({ setState }: { setState: any }) => void;
			setRouteName: (routeName: string) => ({ setState }: { setState: any }) => void;
			setRouteGroup: (routeGroup: string) => ({ setState }: { setState: any }) => void;
		}
	>
> = createActionsHook(Store);

export const useInProductHelpArticleId: HookStateFunction<string, void> = createStateHook(Store, {
	selector: (state) => state.articleId,
});

export const useInProductHelpRouteName: HookStateFunction<string, void> = createStateHook(Store, {
	selector: (state) => state.routeName,
});

export const useInProductHelpRouteGroup: HookStateFunction<string, void> = createStateHook(Store, {
	selector: (state) => state.routeGroup,
});

export const InProductHelpProviderContainer: OverrideContainerComponent<InProductHelpContextType> =
	createContainer(Store, {
		onInit:
			() =>
			({ setState }, productState: InProductHelpContextType) => {
				setState(productState);
			},
		onUpdate:
			() =>
			({ setState }, productState: InProductHelpContextType) => {
				setState(productState);
			},
	});

export const InProductHelpProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
	return <InProductHelpProviderContainer>{children}</InProductHelpProviderContainer>;
};
